// Registro de presets de colecciones por industria.
//
// El motor de colecciones (collection_types + collection_items) es agnóstico de
// industria; los presets lo "visten": cada preset es una definición lista para
// insertar como fila de `collection_types` (labels, ruta, icono, schemaJson).
//
// Lo consumen:
//   - el onboarding (POST /api/onboarding/types) al instanciar los tipos elegidos,
//   - el catálogo "¿Qué más quieres gestionar?" del panel (mismos presets),
//   - la generación LLM (el schema del preset es la especificación de salida).
//
// Regla de producto (docs/onboarding.md §1): el usuario nunca ve "colección".
// Aquí solo viven datos del motor; las etiquetas amigables salen de `labels`.

export type Locale = 'es' | 'en';

export type FieldType =
  | 'text' | 'richtext' | 'number' | 'price' | 'boolean'
  | 'select' | 'image' | 'gallery' | 'textlist' | 'videolist' | 'relation'
  | 'coordinates';

export type LocalizedText = Record<Locale, string>;

export interface FieldDef {
  key: string;
  type: FieldType;
  localized?: boolean;
  label: LocalizedText;
  help?: Partial<LocalizedText>;
  example?: Partial<LocalizedText>;
  section?: string;
  required?: boolean;
  default?: unknown;       // valor inicial para ítems nuevos (no afecta edición)
  format?: 'phone' | 'email' | 'url'; // solo text — tipo de input semántico
  options?: string[];      // solo select
  optionLabels?: Record<string, Partial<LocalizedText>>; // labels visibles; el valor no cambia
  relationTo?: string;     // solo relation → key de otro collection_type
}

export type Archetype = 'catalog' | 'listing' | 'people' | 'social' | 'editorial' | 'ops';
export type JsonLdType =
  | 'RealEstateListing' | 'Person' | 'Article' | 'Service' | 'Product' | 'LocalBusiness' | null;

export interface CollectionTypePreset {
  key: string;                                   // = collection_types.key (único por tenant)
  archetype: Archetype;
  labels: { singular: LocalizedText; plural: LocalizedText };
  route: Record<Locale, string>;
  icon: string;                                  // nombre de icono lucide
  jsonLdType: JsonLdType;
  hasDetailPage: boolean;
  schema: FieldDef[];
  // Pista para el LLM: qué es y cómo suena el contenido de esta colección.
  aiHint: { es: string; en: string };
}

export interface IndustryDef {
  key: string;                                   // se guarda en tenants.industry
  label: LocalizedText;
  icon: string;
  blurb: LocalizedText;                          // una línea para la card de industria
  recommended: string[];                         // preset keys pre-seleccionados en el wizard
  compatible: string[];                          // presets que tienen sentido para esta industria
  // Labels que sobreescriben los del preset al instanciar (ej. "Equipo" → "Especialistas").
  labelOverrides?: Record<string, { singular: LocalizedText; plural: LocalizedText }>;
}

// ─────────────────────────── Presets ───────────────────────────

const properties: CollectionTypePreset = {
  key: 'properties',
  archetype: 'listing',
  labels: { singular: { es: 'Propiedad', en: 'Property' }, plural: { es: 'Propiedades', en: 'Properties' } },
  route: { es: 'propiedades', en: 'properties' },
  icon: 'home',
  jsonLdType: 'RealEstateListing',
  hasDetailPage: true,
  aiHint: {
    es: 'Inmueble en venta o renta con descripción atractiva y especificaciones reales.',
    en: 'Property for sale or rent with attractive copy and realistic specs.',
  },
  schema: [
    { key: 'price', type: 'price', localized: false, label: { es: 'Precio', en: 'Price' }, section: 'Básico' },
    { key: 'currency', type: 'select', localized: false, label: { es: 'Moneda', en: 'Currency' }, options: ['MXN', 'USD'], default: 'MXN', section: 'Básico' },
    { key: 'type', type: 'select', localized: false, label: { es: 'Tipo de operación', en: 'Operation type' }, options: ['sale', 'rent'], optionLabels: { sale: { es: 'Venta', en: 'Sale' }, rent: { es: 'Renta', en: 'Rent' } }, section: 'Básico' },
    { key: 'status', type: 'select', localized: false, label: { es: 'Estado comercial', en: 'Listing status' }, help: { es: 'Indica si la propiedad sigue disponible para los visitantes.', en: 'Indicates whether the property is still available to visitors.' }, options: ['available', 'sold', 'rented'], optionLabels: { available: { es: 'Disponible', en: 'Available' }, sold: { es: 'Vendida', en: 'Sold' }, rented: { es: 'Rentada', en: 'Rented' } }, section: 'Básico' },
    { key: 'description', type: 'richtext', localized: true, label: { es: 'Descripción', en: 'Description' }, section: 'Básico' },
    { key: 'location', type: 'text', localized: false, label: { es: 'Ubicación visible', en: 'Display location' }, help: { es: 'Texto público que aparecerá en la tarjeta y la ficha de la propiedad.', en: 'Public text shown on the property card and detail page.' }, example: { es: 'Fracc. El Lago, Tijuana', en: 'El Lago, Tijuana' }, section: 'Ubicación' },
    { key: 'city', type: 'text', localized: false, label: { es: 'Ciudad o municipio', en: 'City or municipality' }, help: { es: 'Se usa para organizar y filtrar propiedades.', en: 'Used to organize and filter properties.' }, example: { es: 'Tijuana', en: 'Tijuana' }, section: 'Ubicación' },
    { key: 'neighborhood', type: 'text', localized: false, label: { es: 'Colonia o fraccionamiento', en: 'Neighborhood or development' }, help: { es: 'Zona específica dentro de la ciudad o municipio.', en: 'Specific area within the city or municipality.' }, example: { es: 'Fracc. El Lago', en: 'El Lago' }, section: 'Ubicación' },
    { key: 'coordinates', type: 'coordinates', localized: false, label: { es: 'Coordenadas', en: 'Coordinates' }, help: { es: 'Ubica la propiedad en el mapa. Puedes ocultar la dirección exacta por privacidad del dueño.', en: 'Pin the property on the map. You can hide the exact address for owner privacy.' }, section: 'Ubicación' },
    { key: 'bedrooms', type: 'number', localized: false, label: { es: 'Recámaras', en: 'Bedrooms' }, section: 'Detalles' },
    { key: 'bathrooms', type: 'number', localized: false, label: { es: 'Baños', en: 'Bathrooms' }, section: 'Detalles' },
    { key: 'parkingSpaces', type: 'number', localized: false, label: { es: 'Estacionamientos', en: 'Parking' }, section: 'Detalles' },
    { key: 'area', type: 'number', localized: false, label: { es: 'Superficie principal', en: 'Primary area' }, help: { es: 'Medida principal que se mostrará en tarjetas y resultados.', en: 'Primary measurement shown on cards and results.' }, example: { es: '195', en: '195' }, section: 'Detalles' },
    { key: 'areaUnit', type: 'select', localized: false, label: { es: 'Unidad de superficie', en: 'Area unit' }, help: { es: 'Unidad aplicada a superficie, construcción y terreno.', en: 'Unit applied to area, construction and lot measurements.' }, options: ['m²', 'ft²'], default: 'm²', section: 'Detalles' },
    { key: 'propertySubtype', type: 'select', localized: false, label: { es: 'Subtipo', en: 'Subtype' }, options: ['casa', 'departamento', 'terreno', 'local', 'oficina', 'bodega', 'rancho'], optionLabels: { casa: { es: 'Casa', en: 'House' }, departamento: { es: 'Departamento', en: 'Apartment' }, terreno: { es: 'Terreno', en: 'Land' }, local: { es: 'Local comercial', en: 'Commercial space' }, oficina: { es: 'Oficina', en: 'Office' }, bodega: { es: 'Bodega', en: 'Warehouse' }, rancho: { es: 'Rancho', en: 'Ranch' } }, section: 'Detalles' },
    { key: 'propertyCondition', type: 'select', localized: false, label: { es: 'Condición', en: 'Condition' }, options: ['nueva', 'usada', 'remodelada', 'en_construccion', 'en_planos'], optionLabels: { nueva: { es: 'Nueva', en: 'New' }, usada: { es: 'Usada', en: 'Pre-owned' }, remodelada: { es: 'Remodelada', en: 'Renovated' }, en_construccion: { es: 'En construcción', en: 'Under construction' }, en_planos: { es: 'En planos', en: 'Pre-construction' } }, section: 'Detalles' },
    { key: 'landUse', type: 'select', localized: false, label: { es: 'Uso de suelo', en: 'Land use' }, options: ['habitacional', 'comercial', 'mixto', 'industrial', 'turistico'], optionLabels: { habitacional: { es: 'Habitacional', en: 'Residential' }, comercial: { es: 'Comercial', en: 'Commercial' }, mixto: { es: 'Mixto', en: 'Mixed use' }, industrial: { es: 'Industrial', en: 'Industrial' }, turistico: { es: 'Turístico', en: 'Tourism' } }, section: 'Detalles' },
    { key: 'regime', type: 'select', localized: false, label: { es: 'Régimen', en: 'Regime' }, options: ['condominio', 'privada', 'copropiedad'], optionLabels: { condominio: { es: 'Condominio', en: 'Condominium' }, privada: { es: 'Propiedad privada', en: 'Private property' }, copropiedad: { es: 'Copropiedad', en: 'Co-ownership' } }, section: 'Detalles' },
    { key: 'furnished', type: 'select', localized: false, label: { es: 'Amueblado', en: 'Furnished' }, options: ['sin_amueblar', 'semi_amueblado', 'amueblado'], optionLabels: { sin_amueblar: { es: 'Sin amueblar', en: 'Unfurnished' }, semi_amueblado: { es: 'Semi-amueblado', en: 'Semi-furnished' }, amueblado: { es: 'Amueblado', en: 'Furnished' } }, section: 'Detalles' },
    { key: 'features', type: 'textlist', localized: true, label: { es: 'Características', en: 'Features' }, section: 'Detalles' },
    { key: 'gallery', type: 'gallery', localized: false, label: { es: 'Galería', en: 'Gallery' }, section: 'Imágenes' },
  ],
};

const agents: CollectionTypePreset = {
  key: 'agents',
  archetype: 'people',
  labels: { singular: { es: 'Agente', en: 'Agent' }, plural: { es: 'Agentes', en: 'Agents' } },
  route: { es: 'agentes', en: 'agents' },
  icon: 'users',
  jsonLdType: 'Person',
  hasDetailPage: false,
  aiHint: {
    es: 'Asesor inmobiliario con puesto y una bio breve y profesional.',
    en: 'Real-estate agent with a role and a short professional bio.',
  },
  schema: [
    { key: 'role', type: 'text', localized: false, label: { es: 'Puesto', en: 'Role' }, section: 'Básico' },
    { key: 'photo', type: 'image', localized: false, label: { es: 'Foto', en: 'Photo' }, section: 'Básico' },
    { key: 'bio', type: 'richtext', localized: true, label: { es: 'Biografía', en: 'Bio' }, section: 'Básico' },
    { key: 'email', type: 'text', format: 'email', localized: false, label: { es: 'Email', en: 'Email' }, section: 'Contacto' },
    { key: 'phone', type: 'text', format: 'phone', localized: false, label: { es: 'Teléfono', en: 'Phone' }, section: 'Contacto' },
    { key: 'whatsapp', type: 'text', format: 'phone', localized: false, label: { es: 'WhatsApp', en: 'WhatsApp' }, section: 'Contacto' },
    { key: 'office', type: 'relation', relationTo: 'offices', localized: false, label: { es: 'Sucursal', en: 'Branch' }, section: 'Organización' },
  ],
};

const menu: CollectionTypePreset = {
  key: 'menu',
  archetype: 'catalog',
  labels: { singular: { es: 'Platillo', en: 'Dish' }, plural: { es: 'Menú', en: 'Menu' } },
  route: { es: 'menu', en: 'menu' },
  icon: 'utensils',
  jsonLdType: 'Product',
  hasDetailPage: false,
  aiHint: {
    es: 'Platillo del menú con descripción apetecible y precio.',
    en: 'Menu dish with appetizing copy and a price.',
  },
  schema: [
    { key: 'description', type: 'richtext', localized: true, label: { es: 'Descripción', en: 'Description' }, section: 'Básico' },
    { key: 'price', type: 'price', localized: false, label: { es: 'Precio', en: 'Price' }, section: 'Básico' },
    { key: 'category', type: 'text', localized: false, label: { es: 'Categoría', en: 'Category' }, section: 'Básico' },
    { key: 'image', type: 'image', localized: false, label: { es: 'Foto', en: 'Photo' }, section: 'Imágenes' },
    { key: 'dietary', type: 'textlist', localized: true, label: { es: 'Dietético', en: 'Dietary' }, section: 'Detalles' },
  ],
};

const products: CollectionTypePreset = {
  key: 'products',
  archetype: 'catalog',
  labels: { singular: { es: 'Producto', en: 'Product' }, plural: { es: 'Productos', en: 'Products' } },
  route: { es: 'productos', en: 'products' },
  icon: 'shopping-bag',
  jsonLdType: 'Product',
  hasDetailPage: true,
  aiHint: {
    es: 'Producto a la venta con descripción que vende y precio.',
    en: 'Product for sale with persuasive copy and a price.',
  },
  schema: [
    { key: 'description', type: 'richtext', localized: true, label: { es: 'Descripción', en: 'Description' }, section: 'Básico' },
    { key: 'price', type: 'price', localized: false, label: { es: 'Precio', en: 'Price' }, section: 'Básico' },
    { key: 'currency', type: 'select', localized: false, label: { es: 'Moneda', en: 'Currency' }, options: ['MXN', 'USD'], section: 'Básico' },
    { key: 'sku', type: 'text', localized: false, label: { es: 'SKU', en: 'SKU' }, section: 'Detalles' },
    { key: 'features', type: 'textlist', localized: true, label: { es: 'Características', en: 'Features' }, section: 'Detalles' },
    { key: 'gallery', type: 'gallery', localized: false, label: { es: 'Galería', en: 'Gallery' }, section: 'Imágenes' },
  ],
};

const services: CollectionTypePreset = {
  key: 'services',
  archetype: 'catalog',
  labels: { singular: { es: 'Servicio', en: 'Service' }, plural: { es: 'Servicios', en: 'Services' } },
  route: { es: 'servicios', en: 'services' },
  icon: 'briefcase',
  jsonLdType: 'Service',
  hasDetailPage: true,
  aiHint: {
    es: 'Servicio que ofrece el negocio, con beneficio claro para el cliente.',
    en: 'Service the business offers, with a clear customer benefit.',
  },
  schema: [
    { key: 'summary', type: 'text', localized: true, label: { es: 'Resumen', en: 'Summary' }, section: 'Básico' },
    { key: 'description', type: 'richtext', localized: true, label: { es: 'Descripción', en: 'Description' }, section: 'Básico' },
    { key: 'price', type: 'price', localized: false, label: { es: 'Precio (opcional)', en: 'Price (optional)' }, section: 'Básico' },
    { key: 'icon', type: 'text', localized: false, label: { es: 'Icono', en: 'Icon' }, section: 'Básico' },
    { key: 'features', type: 'textlist', localized: true, label: { es: 'Incluye', en: 'Includes' }, section: 'Detalles' },
    { key: 'image', type: 'image', localized: false, label: { es: 'Imagen', en: 'Image' }, section: 'Imágenes' },
  ],
};

const team: CollectionTypePreset = {
  key: 'team',
  archetype: 'people',
  labels: { singular: { es: 'Persona', en: 'Person' }, plural: { es: 'Equipo', en: 'Team' } },
  route: { es: 'equipo', en: 'team' },
  icon: 'users',
  jsonLdType: 'Person',
  hasDetailPage: false,
  aiHint: {
    es: 'Integrante del equipo con puesto y bio breve y cercana.',
    en: 'Team member with a role and a short, warm bio.',
  },
  schema: [
    { key: 'role', type: 'text', localized: false, label: { es: 'Puesto', en: 'Role' }, section: 'Básico' },
    { key: 'photo', type: 'image', localized: false, label: { es: 'Foto', en: 'Photo' }, section: 'Básico' },
    { key: 'bio', type: 'richtext', localized: true, label: { es: 'Biografía', en: 'Bio' }, section: 'Básico' },
    { key: 'email', type: 'text', format: 'email', localized: false, label: { es: 'Email', en: 'Email' }, section: 'Contacto' },
    { key: 'phone', type: 'text', format: 'phone', localized: false, label: { es: 'Teléfono', en: 'Phone' }, section: 'Contacto' },
  ],
};

const testimonials: CollectionTypePreset = {
  key: 'testimonials',
  archetype: 'social',
  labels: { singular: { es: 'Testimonio', en: 'Testimonial' }, plural: { es: 'Testimonios', en: 'Testimonials' } },
  route: { es: 'testimonios', en: 'testimonials' },
  icon: 'message-square-quote',
  jsonLdType: null,
  hasDetailPage: false,
  aiHint: {
    es: 'Reseña de un cliente satisfecho, en primera persona y creíble.',
    en: 'Review from a happy customer, first person and believable.',
  },
  schema: [
    { key: 'role', type: 'text', localized: false, label: { es: 'Puesto / Empresa', en: 'Role / Company' }, section: 'Básico' },
    { key: 'quote', type: 'richtext', localized: true, label: { es: 'Testimonio', en: 'Quote' }, section: 'Básico' },
    { key: 'rating', type: 'number', localized: false, label: { es: 'Calificación (1-5)', en: 'Rating (1-5)' }, section: 'Básico' },
    { key: 'avatar', type: 'image', localized: false, label: { es: 'Foto', en: 'Photo' }, section: 'Básico' },
  ],
};

const faq: CollectionTypePreset = {
  key: 'faq',
  archetype: 'ops',
  labels: { singular: { es: 'Pregunta', en: 'Question' }, plural: { es: 'Preguntas frecuentes', en: 'FAQ' } },
  route: { es: 'preguntas-frecuentes', en: 'faq' },
  icon: 'help-circle',
  jsonLdType: null,
  hasDetailPage: false,
  aiHint: {
    es: 'Pregunta frecuente con una respuesta clara y útil.',
    en: 'Frequently asked question with a clear, helpful answer.',
  },
  schema: [
    { key: 'answer', type: 'richtext', localized: true, label: { es: 'Respuesta', en: 'Answer' }, section: 'Básico' },
    { key: 'category', type: 'text', localized: false, label: { es: 'Categoría', en: 'Category' }, section: 'Básico' },
  ],
};

const offices: CollectionTypePreset = {
  key: 'offices',
  archetype: 'ops',
  labels: { singular: { es: 'Sucursal', en: 'Office' }, plural: { es: 'Sucursales', en: 'Offices' } },
  route: { es: 'sucursales', en: 'offices' },
  icon: 'map-pin',
  jsonLdType: 'LocalBusiness',
  hasDetailPage: false,
  aiHint: {
    es: 'Sucursal u oficina física del negocio con dirección, horarios y datos de contacto propios.',
    en: 'Physical branch or office with its own address, hours, and contact details.',
  },
  schema: [
    { key: 'address', type: 'text', localized: false, label: { es: 'Dirección visible', en: 'Display address' }, help: { es: 'Dirección que aparecerá en el sitio.', en: 'Address shown on the site.' }, section: 'Ubicación' },
    { key: 'coordinates', type: 'coordinates', localized: false, label: { es: 'Mapa', en: 'Map' }, help: { es: 'Ubica la sucursal en el mapa.', en: 'Pin this office on the map.' }, section: 'Ubicación' },
    { key: 'hours', type: 'text', localized: true, label: { es: 'Horario', en: 'Hours' }, help: { es: 'Ej: Lun-Vie 9–18 h, Sáb 10–14 h', en: 'E.g. Mon–Fri 9 am–6 pm, Sat 10 am–2 pm' }, section: 'Horario' },
    { key: 'phone', type: 'text', format: 'phone', localized: false, label: { es: 'Teléfono', en: 'Phone' }, section: 'Contacto' },
    { key: 'whatsapp', type: 'text', format: 'phone', localized: false, label: { es: 'WhatsApp', en: 'WhatsApp' }, section: 'Contacto' },
    { key: 'email', type: 'text', format: 'email', localized: false, label: { es: 'Email', en: 'Email' }, section: 'Contacto' },
    { key: 'isMain', type: 'boolean', localized: false, label: { es: 'Sede principal', en: 'Main office' }, help: { es: 'Marca si esta es la oficina o sede principal del negocio.', en: 'Mark if this is the main business location.' }, section: 'Estado' },
  ],
};

const posts: CollectionTypePreset = {
  key: 'posts',
  archetype: 'editorial',
  labels: { singular: { es: 'Artículo', en: 'Post' }, plural: { es: 'Blog', en: 'Blog' } },
  route: { es: 'blog', en: 'blog' },
  icon: 'newspaper',
  jsonLdType: 'Article',
  hasDetailPage: true,
  aiHint: {
    es: 'Artículo de blog con extracto y cuerpo de varios párrafos, tono experto y cercano.',
    en: 'Blog post with an excerpt and a multi-paragraph body, expert yet warm tone.',
  },
  schema: [
    { key: 'excerpt', type: 'richtext', localized: true, label: { es: 'Extracto', en: 'Excerpt' }, section: 'Básico' },
    { key: 'body', type: 'richtext', localized: true, label: { es: 'Contenido', en: 'Content' }, section: 'Básico' },
    { key: 'cover', type: 'image', localized: false, label: { es: 'Portada', en: 'Cover' }, section: 'Imágenes' },
    { key: 'category', type: 'text', localized: false, label: { es: 'Categoría', en: 'Category' }, section: 'Básico' },
    { key: 'tags', type: 'textlist', localized: false, label: { es: 'Etiquetas', en: 'Tags' }, section: 'Básico' },
  ],
};

type FieldGuidance = Pick<FieldDef, 'help' | 'example'>;

// La orientación pertenece a la combinación colección/campo. No se infiere por
// tipo porque una misma key puede tener propósitos distintos entre industrias.
const FIELD_GUIDANCE: Record<string, Record<string, FieldGuidance>> = {
  properties: {
    price: { help: { es: 'Precio numérico de venta o renta, sin símbolos ni separadores.', en: 'Numeric sale or rental price, without symbols or separators.' } },
    currency: { help: { es: 'Moneda en la que se expresa el precio.', en: 'Currency used for the price.' } },
    description: { help: { es: 'Descripción comercial completa que verá el visitante.', en: 'Full marketing description shown to visitors.' } },
    bedrooms: { help: { es: 'Número total de recámaras de la propiedad.', en: 'Total number of bedrooms in the property.' } },
    bathrooms: { help: { es: 'Número de baños completos.', en: 'Number of full bathrooms.' } },
    parkingSpaces: { help: { es: 'Cantidad de vehículos que caben en el estacionamiento.', en: 'Number of vehicles that fit in the parking area.' } },
    propertySubtype: { help: { es: 'Clase específica de inmueble, por ejemplo casa, departamento o terreno.', en: 'Specific property class, such as house, apartment, or lot.' } },
    propertyCondition: { help: { es: 'Estado físico o etapa actual del inmueble.', en: 'Current physical condition or development stage of the property.' } },
    landUse: { help: { es: 'Uso de suelo legal o permitido para el predio.', en: 'Legal or permitted land use for the property.' } },
    regime: { help: { es: 'Régimen legal o administrativo, por ejemplo condominio o propiedad privada.', en: 'Legal or administrative regime, such as condominium or private property.' } },
    furnished: { help: { es: 'Indica si se entrega amueblada y en qué nivel.', en: 'Indicates whether and to what extent the property is furnished.' } },
    features: { help: { es: 'Amenidades o atributos destacados, uno por renglón.', en: 'Highlighted amenities or attributes, one per line.' } },
    gallery: { help: { es: 'Imágenes públicas de la propiedad; la primera funciona como portada.', en: 'Public property images; the first one is used as the cover.' } },
  },
  agents: {
    role: { help: { es: 'Cargo profesional que se mostrará junto al nombre.', en: 'Professional role displayed next to the name.' }, example: { es: 'Asesor inmobiliario', en: 'Real-estate advisor' } },
    photo: { help: { es: 'Retrato público del agente o asesor.', en: 'Public portrait of the agent or advisor.' } },
    bio: { help: { es: 'Presentación profesional breve para generar confianza.', en: 'Short professional introduction intended to build trust.' } },
    email: { help: { es: 'Correo público de contacto del agente.', en: 'Agent public contact email.' }, example: { es: 'nombre@empresa.com', en: 'name@company.com' } },
    phone: { help: { es: 'Teléfono de contacto; incluye lada cuando sea necesario.', en: 'Contact phone number; include area or country code when needed.' }, example: { es: '+52 664 123 4567', en: '+1 619 555 0100' } },
    whatsapp: { help: { es: 'Número que recibirá mensajes de WhatsApp, con código de país.', en: 'Number that receives WhatsApp messages, including country code.' }, example: { es: '+52 664 123 4567', en: '+1 619 555 0100' } },
    office: { help: { es: 'Sucursal a la que pertenece este agente. Requiere tener al menos una sucursal creada.', en: 'Branch this agent belongs to. Requires at least one office to be created first.' } },
  },
  menu: {
    description: { help: { es: 'Descripción breve y apetecible del platillo.', en: 'Short, appealing description of the dish.' } },
    price: { help: { es: 'Precio del platillo, sin símbolo de moneda.', en: 'Dish price, without a currency symbol.' } },
    category: { help: { es: 'Sección del menú donde se agrupará el platillo.', en: 'Menu section used to group this dish.' }, example: { es: 'Entradas', en: 'Starters' } },
    image: { help: { es: 'Fotografía principal que representa el platillo.', en: 'Main photograph representing the dish.' } },
    dietary: { help: { es: 'Indicaciones como vegano, vegetariano o sin gluten.', en: 'Labels such as vegan, vegetarian, or gluten-free.' } },
  },
  products: {
    description: { help: { es: 'Descripción comercial completa del producto.', en: 'Full marketing description of the product.' } },
    price: { help: { es: 'Precio numérico del producto, sin símbolos.', en: 'Numeric product price, without symbols.' } },
    currency: { help: { es: 'Moneda en la que se expresa el precio.', en: 'Currency used for the price.' } },
    sku: { help: { es: 'Código interno único para identificar el producto.', en: 'Unique internal code used to identify the product.' }, example: { es: 'CAM-NEG-M', en: 'SHIRT-BLK-M' } },
    features: { help: { es: 'Características o beneficios principales, uno por renglón.', en: 'Main features or benefits, one per line.' } },
    gallery: { help: { es: 'Imágenes del producto; la primera funciona como portada.', en: 'Product images; the first one is used as the cover.' } },
  },
  services: {
    summary: { help: { es: 'Frase corta para tarjetas y listados del servicio.', en: 'Short sentence used on service cards and listings.' } },
    description: { help: { es: 'Explicación completa del servicio y su beneficio.', en: 'Full explanation of the service and its benefit.' } },
    price: { help: { es: 'Precio de referencia; déjalo vacío si requiere cotización.', en: 'Reference price; leave empty when a quote is required.' } },
    icon: { help: { es: 'Nombre del icono Lucide usado para representar el servicio.', en: 'Lucide icon name used to represent the service.' }, example: { es: 'briefcase', en: 'briefcase' } },
    features: { help: { es: 'Entregables o beneficios incluidos, uno por renglón.', en: 'Included deliverables or benefits, one per line.' } },
    image: { help: { es: 'Imagen principal que representa el servicio.', en: 'Main image representing the service.' } },
  },
  team: {
    role: { help: { es: 'Cargo que se mostrará junto al nombre de la persona.', en: 'Role displayed next to the person name.' } },
    photo: { help: { es: 'Retrato público del integrante del equipo.', en: 'Public portrait of the team member.' } },
    bio: { help: { es: 'Presentación breve de su experiencia y responsabilidades.', en: 'Short introduction to their experience and responsibilities.' } },
    email: { help: { es: 'Correo público de contacto.', en: 'Public contact email.' } },
    phone: { help: { es: 'Teléfono público de contacto.', en: 'Public contact phone number.' } },
  },
  testimonials: {
    role: { help: { es: 'Puesto, empresa o contexto que identifica a quien opina.', en: 'Role, company, or context identifying the reviewer.' } },
    quote: { help: { es: 'Texto del testimonio tal como se mostrará públicamente.', en: 'Testimonial text as it will be displayed publicly.' } },
    rating: { help: { es: 'Calificación numérica de 1 a 5.', en: 'Numeric rating from 1 to 5.' } },
    avatar: { help: { es: 'Foto opcional de la persona que dio el testimonio.', en: 'Optional photo of the person who provided the testimonial.' } },
  },
  faq: {
    answer: { help: { es: 'Respuesta clara y directa a la pregunta usada como título.', en: 'Clear, direct answer to the question used as the title.' } },
    category: { help: { es: 'Tema utilizado para agrupar preguntas relacionadas.', en: 'Topic used to group related questions.' }, example: { es: 'Pagos', en: 'Payments' } },
  },
  posts: {
    excerpt: { help: { es: 'Resumen corto mostrado en tarjetas, listados y resultados.', en: 'Short summary shown on cards, listings, and results.' } },
    body: { help: { es: 'Contenido completo del artículo que verá el lector.', en: 'Full article content shown to the reader.' } },
    cover: { help: { es: 'Imagen principal del artículo para tarjetas y encabezado.', en: 'Main article image used on cards and the header.' } },
    category: { help: { es: 'Tema principal usado para organizar el blog.', en: 'Primary topic used to organize the blog.' }, example: { es: 'Consejos inmobiliarios', en: 'Real-estate advice' } },
    tags: { help: { es: 'Palabras clave más específicas, una por renglón.', en: 'More specific keywords, one per line.' } },
  },
  offices: {
    address: { help: { es: 'Dirección completa que verán los visitantes.', en: 'Full address shown to visitors.' }, example: { es: 'Av. Reforma 250, Col. Centro, Tijuana, B.C.', en: '250 Reforma Ave, Downtown, Tijuana, B.C.' } },
    hours: { help: { es: 'Horario de atención de esta sucursal.', en: 'Business hours for this location.' }, example: { es: 'Lun–Vie 9–18 h', en: 'Mon–Fri 9 am–6 pm' } },
    phone: { help: { es: 'Teléfono directo de la sucursal; incluye lada.', en: 'Branch direct phone; include area code.' }, example: { es: '+52 664 123 4567', en: '+1 619 555 0100' } },
    whatsapp: { help: { es: 'Número WhatsApp de la sucursal con código de país.', en: 'Branch WhatsApp number with country code.' } },
    email: { help: { es: 'Correo de contacto de esta sucursal.', en: 'Contact email for this branch.' } },
    isMain: { help: { es: 'Solo debe marcarse una sucursal como sede principal.', en: 'Only one location should be marked as main office.' } },
  },
};

function withGuidance(preset: CollectionTypePreset): CollectionTypePreset {
  const guidance = FIELD_GUIDANCE[preset.key] ?? {};
  return {
    ...preset,
    schema: preset.schema.map((field) => ({ ...guidance[field.key], ...field })),
  };
}

// Mapa por key — fuente única para instanciar y para el catálogo "añadir contenido".
export const PRESETS: Record<string, CollectionTypePreset> = {
  properties: withGuidance(properties),
  agents: withGuidance(agents),
  menu: withGuidance(menu),
  products: withGuidance(products),
  services: withGuidance(services),
  team: withGuidance(team),
  testimonials: withGuidance(testimonials),
  faq: withGuidance(faq),
  posts: withGuidance(posts),
  offices: withGuidance(offices),
};

// Presets que sirven a cualquier industria (catálogo "¿Qué más quieres gestionar?").
export const UNIVERSAL_PRESET_KEYS = ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'] as const;

// Industrias ofrecidas en el wizard. `recommended` define qué tipos se preseleccionan.
export const INDUSTRIES: IndustryDef[] = [
  {
    key: 'inmobiliaria',
    label: { es: 'Inmobiliaria', en: 'Real estate' },
    icon: 'home',
    blurb: { es: 'Propiedades en venta o renta y tu equipo de asesores.', en: 'Properties for sale or rent and your team of agents.' },
    recommended: ['properties', 'agents', 'posts'],
    compatible:  ['properties', 'agents', 'posts', 'testimonials', 'faq', 'offices', 'services', 'team'],
    labelOverrides: {
      team: {
        singular: { es: 'Colaborador',   en: 'Team member' },
        plural:   { es: 'Colaboradores', en: 'Team members' },
      },
    },
  },
  {
    key: 'restaurante',
    label: { es: 'Restaurante', en: 'Restaurant' },
    icon: 'utensils',
    blurb: { es: 'Tu menú, reseñas de clientes y dudas frecuentes.', en: 'Your menu, customer reviews and FAQ.' },
    recommended: ['menu', 'testimonials', 'faq', 'posts'],
    compatible:  ['menu', 'testimonials', 'faq', 'offices', 'team', 'posts'],
    labelOverrides: {
      team: {
        singular: { es: 'Colaborador', en: 'Staff member' },
        plural:   { es: 'Personal',    en: 'Staff' },
      },
    },
  },
  {
    key: 'salud',
    label: { es: 'Salud / Clínica', en: 'Health / Clinic' },
    icon: 'stethoscope',
    blurb: { es: 'Tratamientos, especialistas y testimonios de pacientes.', en: 'Treatments, specialists and patient testimonials.' },
    recommended: ['services', 'team', 'testimonials', 'faq', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'offices', 'posts'],
    labelOverrides: {
      team: {
        singular: { es: 'Especialista', en: 'Specialist' },
        plural:   { es: 'Especialistas', en: 'Specialists' },
      },
    },
  },
  {
    key: 'servicios',
    label: { es: 'Servicios profesionales', en: 'Professional services' },
    icon: 'briefcase',
    blurb: { es: 'Tus servicios, tu equipo y un blog para posicionarte.', en: 'Your services, your team and a blog to stand out.' },
    recommended: ['services', 'team', 'testimonials', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'posts', 'faq', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Profesional',   en: 'Professional' },
        plural:   { es: 'Profesionales', en: 'Professionals' },
      },
    },
  },
  {
    key: 'comercio',
    label: { es: 'Comercio / Tienda', en: 'Commerce / Shop' },
    icon: 'shopping-bag',
    blurb: { es: 'Catálogo de productos, reseñas y dudas frecuentes.', en: 'Product catalog, reviews and FAQ.' },
    recommended: ['products', 'testimonials', 'faq', 'posts'],
    compatible:  ['products', 'services', 'testimonials', 'faq', 'offices', 'team', 'posts'],
    labelOverrides: {
      team: {
        singular: { es: 'Colaborador',   en: 'Team member' },
        plural:   { es: 'Colaboradores', en: 'Team members' },
      },
    },
  },
  {
    key: 'belleza',
    label: { es: 'Belleza / Estética', en: 'Beauty / Salon' },
    icon: 'scissors',
    blurb: { es: 'Salón, spa, barbería o estética con tus servicios y reseñas.', en: 'Salon, spa or barbershop with your services and reviews.' },
    recommended: ['services', 'team', 'testimonials', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'products'],
    labelOverrides: {
      team: {
        singular: { es: 'Estilista',   en: 'Stylist' },
        plural:   { es: 'Estilistas',  en: 'Stylists' },
      },
    },
  },
  {
    key: 'educacion',
    label: { es: 'Educación', en: 'Education' },
    icon: 'graduation-cap',
    blurb: { es: 'Academia, cursos o centro de capacitación con tu oferta educativa.', en: 'Academy, courses or training center with your educational offer.' },
    recommended: ['services', 'team', 'testimonials', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Instructor',   en: 'Instructor' },
        plural:   { es: 'Instructores', en: 'Instructors' },
      },
      services: {
        singular: { es: 'Curso',   en: 'Course' },
        plural:   { es: 'Cursos',  en: 'Courses' },
      },
    },
  },
  {
    key: 'fitness',
    label: { es: 'Fitness / Bienestar', en: 'Fitness / Wellness' },
    icon: 'dumbbell',
    blurb: { es: 'Gimnasio, yoga, pilates o entrenamiento personal.', en: 'Gym, yoga, pilates or personal training.' },
    recommended: ['services', 'team', 'testimonials', 'faq'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Entrenador',   en: 'Trainer' },
        plural:   { es: 'Entrenadores', en: 'Trainers' },
      },
      services: {
        singular: { es: 'Clase',   en: 'Class' },
        plural:   { es: 'Clases',  en: 'Classes' },
      },
    },
  },
  {
    key: 'veterinaria',
    label: { es: 'Veterinaria / Mascotas', en: 'Veterinary / Pets' },
    icon: 'paw-print',
    blurb: { es: 'Clínica veterinaria, grooming o tienda de mascotas.', en: 'Veterinary clinic, grooming or pet shop.' },
    recommended: ['services', 'team', 'testimonials', 'faq'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'products', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Veterinario',   en: 'Veterinarian' },
        plural:   { es: 'Veterinarios',  en: 'Veterinarians' },
      },
    },
  },
  {
    key: 'automotriz',
    label: { es: 'Automotriz', en: 'Automotive' },
    icon: 'wrench',
    blurb: { es: 'Taller mecánico, agencia o detailing con tus servicios.', en: 'Auto shop, dealership or detailing with your services.' },
    recommended: ['services', 'team', 'testimonials', 'faq'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Técnico',   en: 'Technician' },
        plural:   { es: 'Técnicos',  en: 'Technicians' },
      },
    },
  },
  {
    key: 'construccion',
    label: { es: 'Construcción / Diseño', en: 'Construction / Design' },
    icon: 'hard-hat',
    blurb: { es: 'Constructora, arquitecto o interiorista con tu portafolio.', en: 'Construction firm, architect or interior designer with your portfolio.' },
    recommended: ['services', 'team', 'testimonials', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Especialista',   en: 'Specialist' },
        plural:   { es: 'Especialistas',  en: 'Specialists' },
      },
    },
  },
  {
    key: 'turismo',
    label: { es: 'Turismo / Hospedaje', en: 'Tourism / Hospitality' },
    icon: 'compass',
    blurb: { es: 'Hotel boutique, tour operator o agencia de viajes.', en: 'Boutique hotel, tour operator or travel agency.' },
    recommended: ['services', 'testimonials', 'posts', 'faq'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Guía',   en: 'Guide' },
        plural:   { es: 'Guías',  en: 'Guides' },
      },
      services: {
        singular: { es: 'Tour',   en: 'Tour' },
        plural:   { es: 'Tours',  en: 'Tours' },
      },
    },
  },
  {
    key: 'legal',
    label: { es: 'Legal / Contable', en: 'Legal / Accounting' },
    icon: 'scale',
    blurb: { es: 'Despacho de abogados, notaría o firma contable con tus áreas de práctica.', en: 'Law firm, notary or accounting firm with your practice areas.' },
    recommended: ['services', 'team', 'testimonials', 'posts'],
    compatible:  ['services', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      team: {
        singular: { es: 'Abogado',   en: 'Lawyer' },
        plural:   { es: 'Abogados',  en: 'Lawyers' },
      },
      services: {
        singular: { es: 'Área de práctica',   en: 'Practice area' },
        plural:   { es: 'Áreas de práctica',  en: 'Practice areas' },
      },
    },
  },
  {
    key: 'eventos',
    label: { es: 'Eventos / Bodas', en: 'Events / Weddings' },
    icon: 'calendar',
    blurb: { es: 'Organizador de eventos, fotógrafo, florista o DJ con tu portafolio.', en: 'Event planner, photographer, florist or DJ with your portfolio.' },
    recommended: ['services', 'testimonials', 'posts', 'faq'],
    compatible:  ['services', 'products', 'team', 'testimonials', 'faq', 'posts', 'offices'],
    labelOverrides: {
      services: {
        singular: { es: 'Paquete',   en: 'Package' },
        plural:   { es: 'Paquetes',  en: 'Packages' },
      },
      team: {
        singular: { es: 'Colaborador',   en: 'Collaborator' },
        plural:   { es: 'Colaboradores', en: 'Collaborators' },
      },
    },
  },
  {
    key: 'otro',
    label: { es: 'Otro', en: 'Other' },
    icon: 'sparkles',
    blurb: { es: 'Empieza con lo esencial y añade lo que necesites.', en: 'Start with the essentials and add what you need.' },
    recommended: ['services', 'testimonials', 'faq'],
    compatible:  [], // vacío = sin filtro, se muestran todos los presets disponibles
  },
];

export function getIndustry(key: string | null | undefined): IndustryDef | undefined {
  return INDUSTRIES.find((i) => i.key === key);
}

// Convierte un preset en los campos de una fila de `collection_types` (serializa
// los JSON). El worker añade id/tenantId/createdAt. sortOrder = orden de elección.
export function presetToRow(preset: CollectionTypePreset, sortOrder: number) {
  return {
    key: preset.key,
    archetype: preset.archetype,
    labelsJson: JSON.stringify(preset.labels),
    routeJson: JSON.stringify(preset.route),
    icon: preset.icon,
    jsonLdType: preset.jsonLdType,
    hasDetailPage: preset.hasDetailPage,
    schemaJson: JSON.stringify(preset.schema),
    sortOrder,
  };
}
